import React, { Component } from 'react';
import FormicaInput from '../../custom/FormicaInput';
import RoleHelper from '../../../RoleHelper';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { RootState } from '../../../redux/store';
import { TrackerState } from '../../../redux/reducers/TrackerReducer';
import { changeTracker } from '../../../redux/actions/TrackerActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import AnimatedButton from '../../custom/AnimatedButton';
import MessageUtil from '../../../util/MessageUtil';
import CustomTable from '../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import Icon from '../../../icons/Icon';
import TrackerVariable from '../../../domain/tracker/variable/TrackerVariable';
import TrackerTypeUtil from '../../../util/TrackerTypeUtil';
import TrackerSidebarComponent from './TrackerSidebarComponent';
import TrackerVariableContentComponent from './TrackerVariableContentComponent';
import ValueUtil from '../../../util/ValueUtil';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import LanguageUtil from '../../../util/LanguageUtil';

class TrackerVariableComponent extends Component<Props, State> {
  public state: State = new State();

  public render() {
    const {
      tracker: { tracker },
    } = this.props;
    const { variable, editorVisible, selectedData, name, description } = this.state;
    return (
      <div className={'flex flex-col w-full h-full px-5'}>
        <div className={'w-full flex flex-col lg:flex-row justify-around items-center mt-5 mb-3 gap-y-5 gap-x-5'}>
          <div className={'w-full flex'}>
            <FormicaInput
              onChange={this.onChangeName}
              value={name}
              name={'name'}
              label={'Name'}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.name', defaultMessage: 'Name' })}
              type={'text'}
              disabled={!RoleHelper.canEdit('Tracker')}
            />
          </div>
          <div className={'w-full group flex gap-x-5 gap-y-5'}>
            <FormicaSearchSelectBox
              options={this.variableTypeOptions()}
              onChange={this.onChangeType}
              value={ValueUtil.convertEnumToString(variable.type)}
              rounded={true}
              disabled={!RoleHelper.canEdit('Tracker')}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.type', defaultMessage: 'Type' })}
              label={'Type'}
            />
          </div>
          <div className={'w-full group flex gap-x-5 gap-y-5'}>
            <FormicaInput
              value={description}
              name={'Description'}
              onChange={this.onChangeDescription}
              type={'text'}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.description', defaultMessage: 'Description' })}
              disabled={!RoleHelper.canEdit('Tracker')}
            />
          </div>
          <div className={'h-full flex justify-center items-center'}>
            <AnimatedButton
              text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
              customType={'primary'}
              customSize={'sm'}
              icon={'add'}
              onClick={this.addVariable}
              disabled={!RoleHelper.canEdit('Tracker')}
            />
          </div>
        </div>
        <div className={'flex items-center justify-center h-full !max-h-[calc(100vh-22rem)] scrollable-editor-container overflow-y-scroll'}>
          <CustomTable value={tracker.variables} onRowClick={this.openEditor} columns={this.getColumns()} selectionMode={'single'} />
        </div>
        {editorVisible && <div className={'absolute top-0 left-0 w-full h-full bg-[#D9D9D94D]'}></div>}
        <TrackerSidebarComponent
          visible={editorVisible}
          icon={'add'}
          title={'Add a New Variable'}
          closable
          onHide={this.closeEditor}
          content={
            <TrackerVariableContentComponent
              selectedData={selectedData}
              setSelectedData={this.setSelectedData}
              onHide={this.closeEditor}
              visible={editorVisible}
            />
          }
        />
      </div>
    );
  }

  private onChangeName = (e: React.FormEvent<HTMLInputElement>) => {
    this.setState({ name: e.currentTarget.value });
  };

  private onChangeType = (e: FormicaSelectBoxItem) => {
    this.setState({ variable: e.value as TrackerVariable });
  };

  private onChangeDescription = (e: React.FormEvent<HTMLInputElement>) => {
    this.setState({ description: e.currentTarget.value });
  };

  private variableTypeOptions = () => {
    const {
      tracker: { tracker },
    } = this.props;
    return TrackerTypeUtil.getVariableTypeOptions(tracker.platform).map((option) => ({
      value: option.value,
      label: ValueUtil.convertEnumToString(option.label),
    }));
  };

  private addVariable = (): void => {
    const { variable, name, description } = this.state;
    const {
      tracker: { tracker },
      changeTracker,
    } = this.props;
    if (tracker.stage != 'DRAFT') {
      MessageUtil.warning({
        summary: 'Tracker',
        content: 'Can not add variable when tracker is deployed.',
      });
      return;
    }
    if (name == undefined || variable.type == undefined) {
      return MessageUtil.warning({
        summary: 'Tracker',
        detail: <div>Name and type can not null</div>,
      });
    }

    if (tracker.variables?.find((v: any) => v.name == name) != undefined) {
      return MessageUtil.warning({
        summary: 'Tracker',
        detail: <div>Variable name already exist</div>,
      });
    }
    variable.name = name;
    variable.description = description ?? '';
    tracker.variables.push(variable);
    changeTracker(tracker);
    this.setState({ variable: {} as TrackerVariable, name: undefined, description: undefined });
    return MessageUtil.success({
      summary: 'Tracker',
      detail: <div>Variable created.</div>,
    });
  };

  private removeVariable = (variableToRemove: TrackerVariable): void => {
    const {
      tracker: { tracker },
      changeTracker,
    } = this.props;
    const variableIndex = tracker.variables.findIndex((variable) => variable.name === variableToRemove.name);
    if (variableIndex === -1) {
      return;
    }
    tracker.variables.splice(variableIndex, 1);
    changeTracker(tracker);
    MessageUtil.success({
      summary: 'Tracker',
      detail: <div>Variable removed.</div>,
    });
  };

  private openEditor = (e: any) => {
    this.setState({
      selectedData: e.data,
      editorVisible: true,
    });
  };

  private closeEditor = () => {
    this.setState({
      editorVisible: false,
    });
  };

  private setSelectedData = (data: TrackerVariable) => {
    const {
      tracker: { tracker },
      changeTracker,
    } = this.props;
    const updatedTriggers = tracker.variables.map((trigger) => (trigger.name === this.state.selectedData?.name ? data : trigger));
    const updatedTracker = { ...tracker, variables: updatedTriggers };
    this.setState({ selectedData: data });
    changeTracker(updatedTracker);
  };

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.tracker.editor.variable.table.name', defaultMessage: 'Name' }),
        className: 'min-w-[30%]',
        body: (record: TrackerVariable) => {
          return <CustomTableContentWrapper text={record.name} />;
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: 'pages.tracker.editor.variable.table.type', defaultMessage: 'Type' }),
        className: 'min-w-[30%]',
        body: (record: TrackerVariable) => {
          return <CustomTableContentWrapper text={ValueUtil.convertEnumToString(record.type)} />;
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.tracker.editor.variable.table.description', defaultMessage: 'Description' }),
        className: 'min-w-[33%]',
        body: (record: TrackerVariable) => {
          return <CustomTableContentWrapper text={record.description} />;
        },
      },
      {
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.tracker.editor.variable.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        alignHeader: 'center',
        sortable: false,
        body: (eventVariable: TrackerVariable) => {
          const disabled = !RoleHelper.canEdit('Tracker');
          return (
            <div
              className={'flex w-full h-full items-center justify-center'}
              onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                !disabled &&
                  customConfirm({
                    target: e.currentTarget,
                    accept: () => this.removeVariable(eventVariable),
                    message: LanguageUtil.get({ id: 'messages.confirm.removeVariable', defaultMessage: 'Are you sure remove this variable?' }),
                  });
              }}
            >
              <Icon
                icon={'delete'}
                size={20}
                color={'primary'}
                className={`hover:text-primary text-[#607080] ${disabled ? 'cursor-not-allowed' : 'hover:cursor-pointer'} flex items-center justify-center`}
              />
            </div>
          );
        },
      },
    ];
  };
}

class State {
  variable: TrackerVariable = {} as TrackerVariable;
  name: string | undefined;
  description: string | undefined;
  editorVisible: boolean = false;
  selectedData: TrackerVariable | undefined;
}

interface Props extends FormicaProps, ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  tracker: state.tracker as TrackerState,
});

const mapDispatchToProps = {
  changeTracker,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(TrackerVariableComponent));
